import Store from "@/Store.js"

function uuid() {
	return crypto.randomUUID ? crypto.randomUUID() : String(Math.random());
}

class WDocument {

	static trackTypes = {
		theme: { label: "themes", path: "/track/theme" },
		event: { label: "events", path: "/track/event" },
		place: { label: "places", path: "/track/place" },
		figure: { label: "characters", path: "/track/figure" },
		object: { label: "objects", path: "/track/object" },
		extra: { label: "extras", path: "/track/extra" },
	}

	constructor(d = {}, l = {}) {

		this.uuid = (d.uuid || uuid());
		this.create_ts = (d.create_ts || new Date().toISOString());
		this.update_ts = (d.update_ts || new Date().toISOString());
		this.name = (d.name || l.new_book || Store.l.new_book);
		this.title = (d.title || l.new_book || Store.l.new_book);
		this.note = (d.note || "");
		this.section = (d.section || []).map(a => new WSection(a));
		this.review = (d.review || 0);
		this.updateSections();

		this.trackMap = {};
		this.track = {};
		for (let i in WDocument.trackTypes) this.track[i] = [];
		(d.track || []).forEach(a => {
			let b = new WTrack(a);
			this.track[b.type].push(b);
			this.trackMap[b.uuid] = b;
		});
	}

	toJSON() {
		let track = [];
		for (let i in this.track) track.push(...this.track[i]);
		return {
			app: "CaribeWriter",
			appVersion: "1.0.0",
			uuid: this.uuid,
			create_ts: this.create_ts,
			update_ts: this.update_ts,
			review: this.review,
			name: this.name,
			title: this.title,
			note: this.note,
			track: track,
			section: this.section,
		};
	}

	updateSections() {
		this.section.forEach((a, i) => {
			let maxDepth = (i > 0 ? this.section[i-1].depth + 1 : 0);
			if (a.depth > maxDepth) a.depth = maxDepth;
			a.left = (a.depth > 0);
			a.right = (i > 0 && a.depth < maxDepth);
		});
	}

	findSection(uuid) {
		return this.section.find(a => a.uuid === uuid);
	}

	moveSectionUp(uuid) {
		let idx = this.section.findIndex(a => a.uuid === uuid);
		if (idx === 0 || idx === -1) return;
		let tmp = this.section[idx];
		this.section[idx] = this.section[idx-1];
		this.section[idx-1] = tmp;
		this.updateSections();
	}

	moveSectionDown(uuid) {
		let l = this.section.length - 1;
		let idx = this.section.findIndex(a => a.uuid === uuid);
		if (idx === l || idx === -1) return;
		let tmp = this.section[idx];
		this.section[idx] = this.section[idx+1];
		this.section[idx+1] = tmp;
		this.updateSections();
	}

	moveSectionLeft(uuid) {
		let idx = this.section.findIndex(a => a.uuid === uuid);
		if (idx === 0 || idx === -1) return;
		let s = this.section[idx];
		if (s.depth === 0 || s.depth === this.section[idx-1]) return;
		--s.depth
		this.updateSections();
	}

	moveSectionRight(uuid) {
		let idx = this.section.findIndex(a => a.uuid === uuid);
		if (idx === 0 || idx === -1) return;
		let s = this.section[idx];
		++s.depth
		this.updateSections();
	}

	toggleSectionCollapse(uuid) {
		let section = this.findSection(uuid);
		section.collapse = !section.collapse;
	}

	getTrack(uuid) {
		return this.trackMap[uuid];
	}

	addTrack(type) {
		let i = new WTrack(type);
		this.track[type].push(i);
		this.trackMap[i.uuid] = i;
		return i;
	}

	removeTrack(uuid, type) {
		delete this.trackMap[uuid];
		this.track[type] = this.track[type].filter(a => a.uuid !== uuid);
	}

	moveTrackUp(uuid, type) {
		let list = this.track[type];
		let idx = list.findIndex(a => a.uuid === uuid);
		if (idx === 0 || idx === -1) return;
		let tmp = list[idx];
		list[idx] = list[idx-1];
		list[idx-1] = tmp;
	}

	moveTrackDown(uuid, type) {
		let list = this.track[type];
		let l = list.length - 1;
		let idx = list.findIndex(a => a.uuid === uuid);
		if (idx === l || idx === -1) return;
		let tmp = list[idx];
		list[idx] = list[idx+1];
		list[idx+1] = tmp;
	}

	setNote(note) {
		this.note = note;
	}

	setTitle(title) {
		this.title = title;
	}

	touch() {
		this.update_ts = new Date().toISOString();
	}

	setUuid(uuid) {
		this.uuid = uuid;
	}

	setReview(review) {
		this.review = review;
	}
}

class WRelation {
	constructor(d = {}) {
		this.uuid = (d.uuid || uuid());
		this.note = (d.note || "");
	}

	setNote(note) {
		this.note = note;
	}
}

class WItem {
	constructor(d = {}, b = {}) {
		this.uuid = (d.uuid || uuid());
		this.title = (d.title || b.title || "");
		this.abstract = (d.abstract || b.abstract || "");
		this.body = (d.body || "");
		this.note = (d.note || "");
		this.relations = (d.relations || []).map(a => new WRelation(a));
		this.paragraphs = (d.paragraphs || 0);
		this.words = (d.words || 0);
		this.characters = (d.characters || 0);
	}

	setTitle(title) {
		this.title = title;
	}

	setAbstract(abstract) {
		this.abstract = abstract;
	}

	setBody(body) {
		this.body = body;
	}

	setNote(note) {
		this.note = note;
	}

	setCounters(paragraphs, words, characters) {
		this.paragraphs = paragraphs;
		this.words = words;
		this.characters = characters;
		this.timestamp = new Date().toISOString();
	}

	setRelation(uuid, note = "") {
		let rel = this.getRelation(uuid);
		if (rel) {
			rel.setNote(note);
		} else {
			this.relations.push(new WRelation({ uuid, note }));
		}
	}

	removeRelation(uuid) {
		this.relations = this.relations.filter(a => a.uuid !== uuid);
	}

	moveRelationUp(uuid) {
		let idx = this.relations.findIndex(a => a.uuid === uuid);
		if (idx <= 0) return;
		let tmp = this.relations[idx-1];
		this.relations[idx-1] = this.relations[idx];
		this.relations[idx] = tmp;
	}

	moveRelationDown(uuid) {
		let idx = this.relations.findIndex(a => a.uuid === uuid);
		if (idx === this.relations.length-1 || idx === -1) return;
		let tmp = this.relations[idx+1];
		this.relations[idx+1] = this.relations[idx];
		this.relations[idx] = tmp;
	}

	getRelation(uuid) {
		return this.relations.find(a => a.uuid === uuid);
	}
}

class WTrack extends WItem {
	constructor(d) {
		if (typeof d === "string") d = { type: d };
		super(d);
		this.type = d.type;
	}

	type2class() {
		switch(this.type) {
			case "theme":
				return "mdi-image-outline";
			case "event":
				return "mdi-calendar";
			case "place":
				return "mdi-map-marker";
			case "figure":
				return "mdi-account";
			case "object":
				return "mdi-cube-outline";
			case "extra":
				return "mdi-triangle-outline"
		}
		return "";
	}
}

class WScene extends WItem {

	static new_label = "";

	static states = {
		draft: { label: "draft", color: "#aaaaaa" },
		review: { label: "reviewed_draft", color: "#ffdd00" },
		final: { label: "final_draft", color: "#ec7331" },
		complete: { label: "completed", color: "#009966" },
	};

	constructor(d = {}) {
		super(d, {
			title: WScene.new_label,
		});
		this.state = (d.state || "draft");
		this.timestamp = (d.timestamp || new Date().toISOString());
	}

	getTimestamp() {
		return new Intl.DateTimeFormat('it-IT', { year: 'numeric', month: 'numeric', day: 'numeric',hour: 'numeric', minute: 'numeric', hour12: false }).format(new Date(this.timestamp));
	}

	getStateColor() {
		return WScene.states[this.state].color;
	}

	getStateLabel() {
		return WScene.states[this.state].label;
	}

	setState(state) {
		this.state = state;
	}
}

class WSection {

	static new_label = "";

	constructor(d = {}) {
		this.uuid = (d.uuid || uuid());
		this.title = (d.title || WSection.new_label);
		this.abstract = (d.abstract || "");
		this.note = (d.note || "");
		this.depth = (d.depth || 0);
		this.scene = (d.scene || []).map(a => new WScene(a));
		this.collapse = (d.collapse || false);
		this.left = this.right = false;
	}

	toJSON() {
		return {
			uuid: this.uuid,
			title: this.title,
			abstract: this.abstract,
			note: this.note,
			depth: this.depth,
			scene: this.scene,
			collapse: this.collapse,
		};
	}

	setTitle(title) {
		this.title = title;
	}

	setAbstract(abstract) {
		this.abstract = abstract;
	}

	setNote(note) {
		this.note = note;
	}

	addScene() {
		let i = new WScene();
		this.scene.push(i);
		return i;
	}

	appendScene(scene) {
		this.scene.push(scene);
		return scene;
	}

	findScene(uuid) {
		return this.scene.find(a => a.uuid === uuid);
	}

	moveSceneUp(uuid) {
		let idx = this.scene.findIndex(a => a.uuid === uuid);
		if (idx === 0 || idx === -1) return;
		let tmp = this.scene[idx];
		this.scene[idx] = this.scene[idx-1];
		this.scene[idx-1] = tmp;
	}

	moveSceneDown(uuid) {
		let l = this.scene.length - 1;
		let idx = this.scene.findIndex(a => a.uuid === uuid);
		if (idx === l || idx === -1) return;
		let tmp = this.scene[idx];
		this.scene[idx] = this.scene[idx+1];
		this.scene[idx+1] = tmp;
	}

	removeScene(uuid) {
		this.scene = this.scene.filter(a => a.uuid !== uuid);
	}

	countParagraphs() {
		let c = 0;
		this.scene.forEach(a => c += a.paragraphs);
		return c;
	}

	countWords() {
		let c = 0;
		this.scene.forEach(a => c += a.words);
		return c;
	}

	countCharacters() {
		let c = 0;
		this.scene.forEach(a => c += a.characters);
		return c;
	}
}

export { WDocument, WSection, WScene }
